import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import styles from "./Css/BookLibrary.module.css";
import { useNavigate } from "react-router-dom";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useEbooksByGenreName } from "../../../hooks/useEbooksHooks";

function EbookCategoriesCarousel({ categoryName, categorySlug }) {
  const navigate = useNavigate();
  const { ebooks, isLoading, error } = useEbooksByGenreName(categoryName);

  // Check loading and error states
  if (isLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (error) return null;

  // Use ebooks directly from the hook
  const books = ebooks || [];

  const handleViewMore = (category) => {
    navigate(`/books/${category}`);
  };

  // Function to safely get cover image with fallback options
  const getCoverImage = (book) => {
    // First priority: series cover image if available
    if (book.series && book.series.cover_image_url) {
      return book.series.cover_image_url;
    }

    // Second priority: book's desktop cover image
    if (book.cover_image_url_for_computer) {
      return book.cover_image_url_for_computer;
    }

    // Fallback: mobile cover or a default placeholder
    return book.cover_image_url_for_mbl || "/placeholder-cover.jpg";
  };

  // Function to determine which URL to navigate to
  const getNavigationUrl = (book) => {
    return book.series_id
      ? `/book-details/${book.slug}`
      : `/book-detail/${book.slug}`;
  };

  // Handle case when there are no books
  if (!books.length) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.book_slider_container}>
        <div className={styles.author_header}>
          <h2 className={styles.heading}>{categoryName}</h2>
          <span
            className={styles.view_more}
            onClick={() => handleViewMore(categorySlug)}
          >
            View More {">"}
          </span>
        </div>
        <Swiper
          modules={[Mousewheel, FreeMode]}
          freeMode={{
            enabled: true,
            momentum: true,
            momentumRatio: 1, // smoother momentum
            momentumVelocityRatio: 1, // normal velocity handling
            minimumVelocity: 0.02, // keep this small to allow tiny moves
          }}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 1.5,
            releaseOnEdges: true,
          }}
          speed={400}
          resistance={true} // Allow a bit of resistance (helps feel natural)
          resistanceRatio={0.85}
          breakpoints={{
            320: { slidesPerView: 3, spaceBetween: 10 },
            480: { slidesPerView: 3, spaceBetween: 15 },
            768: { slidesPerView: 4, spaceBetween: 20 },
            1024: { slidesPerView: 6, spaceBetween: 20 },
            1200: { slidesPerView: 7, spaceBetween: 20 },
          }}
        >
          {books.map((book) => {
            // Make sure book is a valid object before trying to use it
            if (!book || typeof book !== "object") return null;

            const coverImage = getCoverImage(book);

            return (
              <SwiperSlide key={book.id}>
                <div className={styles.book_slide}>
                  {/* Add error handling for image loading */}
                  <img
                    src={coverImage}
                    alt={book.title || "Book cover"}
                    style={{ cursor: "pointer" }}
                    className={styles.book_image}
                    onClick={() => navigate(getNavigationUrl(book))}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-cover.jpg"; // Fallback image
                    }}
                  />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}

export default EbookCategoriesCarousel;
